<template>
  <Paginator
    v-model:first="internalFirst"
    v-model:rows="internalRows"
    :total-records="totalRecords"
    :rows-per-page-options="rowsPerPageOptions"
    :template="template"
    :current-page-report-template="currentPageReportTemplate"
    :always-show="alwaysShow"
    :pt="pt"
    :pt-options="ptOptions"
    :unstyled="unstyled"
    @page="onPageChange"
  >
    <!-- Start Template -->
    <template v-if="slots.start" #start>
      <slot name="start" />
    </template>

    <!-- First Page Link Icon -->
    <template v-if="slots.firstpagelinkicon" #firstpagelinkicon="slotProps">
      <slot name="firstpagelinkicon" v-bind="slotProps" />
    </template>

    <!-- Previous Page Link Icon -->
    <template v-if="slots.prevpagelinkicon" #prevpagelinkicon="slotProps">
      <slot name="prevpagelinkicon" v-bind="slotProps" />
    </template>

    <!-- Next Page Link Icon -->
    <template v-if="slots.nextpagelinkicon" #nextpagelinkicon="slotProps">
      <slot name="nextpagelinkicon" v-bind="slotProps" />
    </template>

    <!-- Last Page Link Icon -->
    <template v-if="slots.lastpagelinkicon" #lastpagelinkicon="slotProps">
      <slot name="lastpagelinkicon" v-bind="slotProps" />
    </template>


    <!-- End Template -->
    <template v-if="slots.end" #end>
      <slot name="end" />
    </template>
  </Paginator>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import Paginator from 'primevue/paginator'
import type { PaginatorProps, PaginatorEmits, PaginatorPageEvent } from './types'

const slots = useSlots() as any

const props = withDefaults(defineProps<PaginatorProps>(), {
  totalRecords: 0,
  rows: 10,
  first: 0,
  pageLinkSize: 5,
  showPageLinks: true,
  rowsPerPageOptions: () => [],
  showRowsPerPage: true,
  showFirstLastPage: true,
  showPrevNextPage: true,
  currentPageReportTemplate: '({currentPage} of {totalPages})',
  showCurrentPageReport: false,
  alwaysShowPageLinks: true,
  size: 'normal',
  disabled: false
})

const emit = defineEmits<PaginatorEmits>()

// Internal reactive values for v-model
const internalFirst = computed({
  get: () => props.first,
  set: (value: number) => {
    emit('update:first', value)
  }
})

const internalRows = computed({
  get: () => props.rows,
  set: (value: number) => {
    emit('update:rows', value)
  }
})

// Computed template string based on props
const template = computed(() => {
  const elements: string[] = []
  
  if (props.showFirstLastPage) {
    elements.push('FirstPageLink')
  }
  
  if (props.showPrevNextPage) {
    elements.push('PrevPageLink')
  }
  
  if (props.showPageLinks) {
    elements.push('PageLinks')
  }
  
  if (props.showPrevNextPage) {
    elements.push('NextPageLink')
  }
  
  if (props.showFirstLastPage) {
    elements.push('LastPageLink')
  }
  
  if (props.showRowsPerPage && props.rowsPerPageOptions.length > 0) {
    elements.push('RowsPerPageDropdown')
  }
  
  if (props.showCurrentPageReport) {
    elements.push('CurrentPageReport')
  }
  
  return elements.join(' ')
})

// Always show paginator based on props
const alwaysShow = computed(() => props.alwaysShowPageLinks)

// Pass-through attributes for styling
const pt = computed(() => ({
  root: {
    class: [
      {
        'p-paginator-sm': props.size === 'small',
        'p-paginator-lg': props.size === 'large',
        'p-disabled': props.disabled
      }
    ]
  }
}))

const ptOptions = computed(() => ({
  mergeSections: true,
  mergeProps: true
}))

const unstyled = computed(() => false)

// Event handlers
const onPageChange = (event: any) => {
  const pageEvent: PaginatorPageEvent = {
    first: event.first,
    rows: event.rows,
    page: event.page,
    pageCount: event.pageCount
  }
  
  emit('page', pageEvent)
  
  if (event.rows !== props.rows) {
    emit('rows', {
      first: event.first,
      rows: event.rows
    })
  }
}
</script>
